import type { ReactNode } from 'react';
import './Card.css';

export interface CardProps {
  /** Rendered as a heading when present. Omit for a plain grouping surface. */
  title?: ReactNode;
  /** Top-right slot — a badge, a link, a small action. */
  action?: ReactNode;
  children: ReactNode;
  /**
   * `tone` carries meaning, so it is always paired with text by the caller;
   * a card that signals only by border colour fails a11y.md §2.
   */
  tone?: 'default' | 'warning' | 'danger';
  /** Heading level, so a card inside a section does not break the outline. */
  headingLevel?: 2 | 3 | 4;
  /**
   * Bottom band — a hint, a count, a secondary action. Rendered on
   * `--surface-strip`, which is the prototype's footer/inset fill, and reaching
   * the card's edge rather than sitting inside its padding.
   *
   * Supplying this switches the card to its **sectioned** layout: padding moves
   * from the container onto the header/body/footer so each band can bleed to the
   * border. That is the prototype's own card anatomy for tables and panels, and
   * it cannot be expressed with uniform container padding.
   */
  footer?: ReactNode;
  /**
   * A pending/empty tile: dashed outline, no fill. The prototype uses this
   * instead of a solid card for a slot whose content does not exist yet, which
   * says "nothing here" structurally rather than needing a sentence to say it.
   */
  placeholder?: boolean;
  /**
   * DOM id for the `<section>`, so a card can be a navigation target.
   *
   * Added for the app shell's sub-navigation: a sub-item names a section of a screen, and until it
   * could address one it navigated to the screen it was already on — 25 items that did nothing.
   * An id on the section it names is the smallest thing that makes those items real.
   */
  id?: string;
}

/**
 * A grouping surface (design-system catalogue, Tier 2).
 *
 * `headingLevel` exists because a component that hardcodes `<h3>` produces a
 * broken heading outline wherever it is nested differently — and a broken
 * outline is a screen-reader navigation problem, not a cosmetic one.
 */
export function Card({
  title,
  action,
  children,
  tone = 'default',
  headingLevel = 3,
  footer,
  placeholder = false,
  id,
}: CardProps) {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  const className = [
    'dpr-card',
    `dpr-card--${tone}`,
    // Only a footer needs the sectioned layout today. Deriving it rather than
    // exposing a separate `sectioned` prop keeps the two from disagreeing — a
    // sectioned card with container padding, or a footer inside padding, both
    // look wrong and both would be reachable if the caller set them separately.
    footer ? 'dpr-card--sectioned' : null,
    placeholder ? 'dpr-card--placeholder' : null,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    /* `tabIndex={-1}` when addressable: scrolling a section into view moves the eye but not the
       keyboard, so a sub-nav item would silently strand a keyboard or screen-reader user at the top
       of the page. `-1` makes it programmatically focusable without adding a tab stop. */
    <section className={className} id={id} tabIndex={id ? -1 : undefined}>
      {title || action ? (
        <header className="dpr-card__header">
          {title ? <Heading className="dpr-card__title">{title}</Heading> : null}
          {action ? <div className="dpr-card__action">{action}</div> : null}
        </header>
      ) : null}
      <div className="dpr-card__body">{children}</div>
      {footer ? <footer className="dpr-card__footer">{footer}</footer> : null}
    </section>
  );
}
